import React from "react";
import { FileText, Database, Globe, Zap, Mail, Webhook } from "lucide-react";
import { FaWhatsapp, FaFacebook, FaGoogle, FaSalesforce } from "react-icons/fa";
import { ChannelTile } from "./components/cards";
import { useWarehouseMetrics, WarehouseMetric } from "./warehouse-metrics-api";

// Shared by "Connected Sources" and "Activated Destinations" — a labelled
// row of small tiles, each showing a channel's icon + current count, real
// GET /api/v1/dashboard/warehouse-metrics (connected_source /
// activated_destination groups) matched by metric_key (snake_case of the
// tile's own label).
interface ChannelDef {
  icon: React.ReactNode;
  iconClassName: string;
  label: string;
  key: string;
}

const ChannelGrid: React.FC<{ title: string; description: string; group: string; channels: ChannelDef[] }> = ({
  title,
  description,
  group,
  channels,
}) => {
  const { loading, findMetric, formatValue } = useWarehouseMetrics();
  return (
    <section>
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 mb-3">{description}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {channels.map((c) => {
          const metric: WarehouseMetric | undefined = findMetric(group, [c.key], [c.label]);
          return (
            <ChannelTile
              key={c.label}
              icon={c.icon}
              iconClassName={c.iconClassName}
              label={c.label}
              count={formatValue(metric)}
              loading={loading}
              isSample={metric?.is_sample}
            />
          );
        })}
      </div>
    </section>
  );
};

export const ConnectedSourcesSection: React.FC = () => (
  <ChannelGrid
    title="Connected Sources"
    description="View the systems and data sources currently feeding customer information into ConvoHub."
    group="connected_source"
    channels={[
      { icon: <FileText size={15} />, iconClassName: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", label: "CSV", key: "csv" },
      { icon: <Database size={15} />, iconClassName: "bg-orange-50 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400", label: "SQL", key: "sql" },
      { icon: <FaSalesforce size={15} />, iconClassName: "bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400", label: "Salesforce", key: "salesforce" },
      { icon: <Globe size={15} />, iconClassName: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300", label: "Website", key: "website" },
      { icon: <Zap size={15} />, iconClassName: "bg-purple-50 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400", label: "App Events", key: "app_events" },
      { icon: <FaWhatsapp size={15} />, iconClassName: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400", label: "WhatsApp", key: "whatsapp" },
    ]}
  />
);

export const ActivatedDestinationsSection: React.FC = () => (
  <ChannelGrid
    title="Activated Destinations"
    description="See where unified profiles and audiences are being synced for activation and engagement."
    group="activated_destination"
    channels={[
      { icon: <FaWhatsapp size={15} />, iconClassName: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400", label: "WhatsApp", key: "whatsapp" },
      { icon: <FaFacebook size={15} />, iconClassName: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400", label: "Meta Ads", key: "meta_ads" },
      { icon: <FaGoogle size={15} />, iconClassName: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400", label: "Google Ads", key: "google_ads" },
      { icon: <FaSalesforce size={15} />, iconClassName: "bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400", label: "Salesforce", key: "salesforce" },
      { icon: <Mail size={15} />, iconClassName: "bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400", label: "Email", key: "email" },
      { icon: <Webhook size={15} />, iconClassName: "bg-teal-50 dark:bg-teal-500/15 text-teal-600 dark:text-teal-400", label: "Webhook", key: "webhook" },
    ]}
  />
);
